import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { useCurrentUserQuery } from "@base/model/hooks";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type DailyLogSearchErrorKind,
  getDailyLogSearchErrorKind,
  isAccessErrorKind,
  isCursorErrorKind,
} from "./dailyLogSearchErrors";
import { clearNavigationEntries } from "./dailyLogSearchNavigation";
import { dailyLogSearchProjectKey, dailyLogSearchQueryOptions } from "./dailyLogSearchQueries";
import {
  type ChainRunPurpose,
  type DailyLogSearchChain,
  registerActiveDailyLogSearch,
  useDailyLogSearchChainRefresher,
} from "./dailyLogSearchRefresh";
import type { DailyLogSearchItem } from "./dailyLogSearchSchema";
import type { DailyLogSearchConditions } from "./dailyLogSearchState";
import { createSearchRequestId, getSearchDevice } from "./dailyLogSearchTelemetry";

interface UseDailyLogSearchParams {
  projectId: number;
  conditions: DailyLogSearchConditions;
  /** URL 조건이 잘못됐으면 false — 잘못된 요청을 서버로 보내지 않는다 */
  enabled: boolean;
  /** 상세에서 돌아왔을 때 복원할 페이지 수. 복귀가 아니면 null */
  restorePageCount: number | null;
  /** coordinator가 목록을 교체하기 직전. 보던 위치를 보존하려면 여기서 anchor를 잡는다 */
  onBeforeChainSwap?: () => void;
  /** coordinator가 목록을 교체한 직후. 새 데이터가 이전과 같아 행이 다시 렌더되지 않는 경우에도 불린다 */
  onAfterChainSwap?: () => void;
}

export type DailyLogSearchStatus =
  /** 사용자 확인 전, 또는 잘못된 조건으로 검색을 시작하지 않음 */
  | "idle"
  /** 캐시 없는 첫 조회 */
  | "loading"
  /** 캐시 없이 돌아와 저장된 깊이까지 다시 조회하는 중 */
  | "restoring"
  /** 첫 페이지 실패. 표시할 현재 조건의 결과가 없음 */
  | "error"
  | "success";

/**
 * 마운트 시점에 한 번 정하는 복귀 방식.
 * - cache-hit: 캐시를 먼저 보여 주고 같은 깊이까지 새 chain으로 갱신한다
 * - restoring: 캐시가 없으니 기본 조회를 멈추고 저장된 깊이까지 순서대로 조회한다
 */
type RestorePhase = "undecided" | "cache-hit" | "restoring" | "settled";

function dedupeById(items: DailyLogSearchItem[]): DailyLogSearchItem[] {
  const seen = new Set<number>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function isSameProjectKey(queryKey: readonly unknown[] | undefined, userId: number | undefined, projectId: number) {
  if (!queryKey || userId === undefined) return false;
  const [, keyUserId, keyProjectId] = queryKey;
  return keyUserId === userId && keyProjectId === projectId;
}

/**
 * 적용된 검색 조건 하나의 cursor chain 상태.
 * 입력 중 Suspense 경계가 remount되지 않도록 일반 useInfiniteQuery를 쓴다.
 * 여러 페이지 갱신은 기본 refetch 대신 coordinator(daily-log-search-refresh)만 수행한다.
 */
export function useDailyLogSearch({
  projectId,
  conditions,
  enabled,
  restorePageCount,
  onBeforeChainSwap,
  onAfterChainSwap,
}: UseDailyLogSearchParams) {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUserQuery();
  const userId = currentUser?.user.id;
  const scope = `${userId}:${projectId}`;

  // 403/401 후에는 이 사용자·프로젝트의 보호 검색을 다시 시작하지 않는다.
  const [accessDeniedScope, setAccessDeniedScope] = useState<string | null>(null);
  const isAccessDenied = accessDeniedScope === scope;
  const canSearch = enabled && userId !== undefined && !isAccessDenied;

  // 사용자 ID를 알기 전에는 보호 검색을 시작하지 않는다. key 생성용 0은 실제로 요청되지 않는다.
  const queryOptions = dailyLogSearchQueryOptions({ userId: userId ?? 0, projectId, conditions });
  const { queryKey } = queryOptions;
  const queryKeyHash = JSON.stringify(queryKey);

  const [restoreState, setRestoreState] = useState<{ scope: string; phase: RestorePhase }>({
    scope,
    phase: "undecided",
  });
  const restorePhase = restoreState.scope === scope ? restoreState.phase : "undecided";
  if (restoreState.scope !== scope) {
    setRestoreState({ scope, phase: "undecided" });
  } else if (restorePhase === "undecided" && canSearch) {
    // 기본 조회가 시작되기 전에(첫 커밋 전에) 결정해야 같은 페이지를 두 번 요청하지 않는다.
    const hasCache = queryClient.getQueryData(queryKey) !== undefined;
    const shouldRestore = (restorePageCount ?? 0) > 1;
    setRestoreState({ scope, phase: hasCache ? "cache-hit" : shouldRestore ? "restoring" : "settled" });
  }

  const query = useInfiniteQuery({
    ...queryOptions,
    enabled: canSearch && (restorePhase === "cache-hit" || restorePhase === "settled"),
    // 조건 변경 중에는 직전 행을 유지하고 "Updating results"를 표시한다.
    // 다른 사용자·프로젝트의 행은 placeholder로도 섞지 않는다.
    placeholderData: (previousData, previousQuery) =>
      isSameProjectKey(previousQuery?.queryKey, userId, projectId) ? keepPreviousData(previousData) : undefined,
  });

  const refresher = useDailyLogSearchChainRefresher({ queryKey, projectId, conditions });
  const { run: runRefresher, dismissError: dismissRefresherError } = refresher;

  /* ---------------------------- 계측 ---------------------------- */

  /** 화면에 보이는 결과를 만든 조회. 결과 클릭 이벤트에 싣는다 */
  const displayedRequestIdRef = useRef<string | null>(null);
  const pendingQueryRequestRef = useRef<{ id: string; startedAt: number } | null>(null);

  const trackCompleted = useCallback(
    (requestId: string, durationMs: number, resultCount: number, source: "query" | ChainRunPurpose) => {
      displayedRequestIdRef.current = requestId;
      EventLogger.result(CLIENT_EVENT.DAILY_LOG_SEARCH_COMPLETED, {
        project_id: String(projectId),
        request_id: requestId,
        duration_ms: Math.round(durationMs),
        result_count: resultCount,
        device: getSearchDevice(),
        source,
      });
    },
    [projectId],
  );

  const onBeforeChainSwapRef = useRef(onBeforeChainSwap);
  onBeforeChainSwapRef.current = onBeforeChainSwap;
  const onAfterChainSwapRef = useRef(onAfterChainSwap);
  onAfterChainSwapRef.current = onAfterChainSwap;

  const runChain = useCallback(
    (purpose: ChainRunPurpose, targetPageCount: number) => {
      const requestId = createSearchRequestId();
      return runRefresher(purpose, {
        targetPageCount,
        onBeforeApply: () => onBeforeChainSwapRef.current?.(),
        onApplied: (chain: DailyLogSearchChain, durationMs) => {
          onAfterChainSwapRef.current?.();
          const lastPage = chain.pages[chain.pages.length - 1];
          trackCompleted(requestId, durationMs, lastPage?.count ?? 0, purpose);
        },
      });
    },
    [runRefresher, trackCompleted],
  );

  /* ------------------------- 복귀·갱신 흐름 ------------------------- */

  // 복귀 방식이 정해진 순간 한 번만 실행한다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: phase 전환에만 반응
  useEffect(
    function runRestorePhase() {
      if (restorePhase === "cache-hit") {
        const cachedPageCount = queryClient.getQueryData<DailyLogSearchChain>(queryKey)?.pages.length ?? 1;
        setRestoreState({ scope, phase: "settled" });
        void runChain("refresh", Math.max(cachedPageCount, restorePageCount ?? 0));
      }
      if (restorePhase === "restoring") {
        void runChain("restore", restorePageCount ?? 1).then((result) => {
          if (result === "applied") setRestoreState({ scope, phase: "settled" });
        });
      }
    },
    [restorePhase],
  );

  const previousKeyHashRef = useRef(queryKeyHash);
  // biome-ignore lint/correctness/useExhaustiveDependencies: 조건(key) 변경에만 반응
  useEffect(
    function handleConditionChange() {
      if (previousKeyHashRef.current === queryKeyHash) return;
      previousKeyHashRef.current = queryKeyHash;
      pendingQueryRequestRef.current = null;

      // 복구 중 조건을 바꾸면 복구를 버리고 새 조건의 첫 결과부터 조회한다 (refresher는 자동 취소됨).
      if (restorePhase === "restoring") {
        setRestoreState({ scope, phase: "settled" });
        return;
      }
      // 저장 후 stale로 표시된 다른 조건의 캐시로 돌아오면 같은 깊이까지 새로 만든다.
      const cachedState = queryClient.getQueryState<DailyLogSearchChain>(queryKey);
      if (restorePhase === "settled" && canSearch && cachedState?.data && cachedState.isInvalidated) {
        void runChain("refresh", cachedState.data.pages.length);
      }
    },
    [queryKeyHash],
  );

  const isFetchingFirstChain = query.isFetching && !query.isFetchingNextPage;
  useEffect(
    function trackQueryRequestStart() {
      if (isFetchingFirstChain && pendingQueryRequestRef.current === null) {
        pendingQueryRequestRef.current = { id: createSearchRequestId(), startedAt: performance.now() };
      }
    },
    [isFetchingFirstChain],
  );

  const firstPageCount = query.data?.pages[0]?.count;
  useEffect(
    function trackQueryRequestCompleted() {
      const pending = pendingQueryRequestRef.current;
      if (!pending || query.isFetching) return;
      pendingQueryRequestRef.current = null;
      if (query.isSuccess && !query.isPlaceholderData) {
        trackCompleted(pending.id, performance.now() - pending.startedAt, firstPageCount ?? 0, "query");
      }
    },
    [query.isFetching, query.isSuccess, query.isPlaceholderData, firstPageCount, trackCompleted],
  );

  /* ---------------------------- 결과 ---------------------------- */

  const pages = query.data?.pages;
  const items = useMemo(() => dedupeById(pages?.flatMap((page) => page.results) ?? []), [pages]);
  /** 현재 chain의 마지막 성공 응답 count. 불러온 행 수가 아니다 */
  const count = pages?.[pages.length - 1]?.count ?? 0;
  const loadedPageCount = pages?.length ?? 0;

  const queryErrorKind: DailyLogSearchErrorKind | null = query.error ? getDailyLogSearchErrorKind(query.error) : null;
  const refresherErrorKind = refresher.state.status === "error" ? refresher.state.errorKind : null;
  const accessErrorKind = [queryErrorKind, refresherErrorKind].find((kind) => kind !== null && isAccessErrorKind(kind));

  useEffect(
    function denyAccessOnForbidden() {
      if (accessErrorKind && userId !== undefined) setAccessDeniedScope(`${userId}:${projectId}`);
    },
    [accessErrorKind, userId, projectId],
  );

  useEffect(
    function removeProtectedCacheOnAccessDenied() {
      if (!isAccessDenied || userId === undefined) return;
      // 검색이 비활성화된 뒤에 지워야 활성 query가 즉시 재요청하지 않는다.
      queryClient.removeQueries({ queryKey: dailyLogSearchProjectKey(userId, projectId) });
      clearNavigationEntries({ userId, projectId });
    },
    [isAccessDenied, queryClient, userId, projectId],
  );

  const nextPageErrorKind = query.isFetchNextPageError ? queryErrorKind : null;
  const isCursorBroken = nextPageErrorKind !== null && isCursorErrorKind(nextPageErrorKind);
  const isRefresherRunning = refresher.state.status === "running";
  const isRefreshing = isRefresherRunning && refresher.state.purpose === "refresh";
  const refreshErrorKind = refresher.state.purpose === "refresh" ? refresherErrorKind : null;
  const restoreErrorKind = refresher.state.purpose === "restore" ? refresherErrorKind : null;
  const isUpdating = enabled && query.isPlaceholderData;

  const { fetchNextPage, hasNextPage, isFetching } = query;
  const canLoadMore = hasNextPage && !isFetching && !isCursorBroken && !isUpdating && !isRefresherRunning;

  const loadMore = useCallback(() => {
    if (!canLoadMore) return;
    fetchNextPage();
  }, [canLoadMore, fetchNextPage]);

  const runRestore = useCallback(async () => {
    const result = await runChain("restore", restorePageCount ?? 1);
    if (result === "applied") setRestoreState({ scope, phase: "settled" });
  }, [runChain, restorePageCount, scope]);

  const retryRestore = useCallback(() => {
    void runRestore();
  }, [runRestore]);

  /** 조건은 유지하고 첫 cursor부터 불러온 깊이까지 새 체인으로 다시 조회해 한 번에 교체한다 */
  const refresh = useCallback(async () => {
    if (!canSearch || isRefresherRunning) return;
    dismissRefresherError();
    // 캐시 없는 복원이 끝나지 않았으면(실패 후 포함) 복원을 처음부터 다시 한다.
    // 일반 갱신으로 처리하면 성공해도 restoring 상태가 풀리지 않고 일반 조회도 꺼진 채로 남는다.
    if (restorePhase === "restoring") {
      await runRestore();
      return;
    }
    await runChain("refresh", Math.max(1, loadedPageCount));
  }, [canSearch, isRefresherRunning, dismissRefresherError, restorePhase, runRestore, runChain, loadedPageCount]);

  // 목록 밖(당겨서 새로고침·행 삭제)의 갱신 요청도 같은 coordinator로 한 번만 처리한다.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(
    function registerAsActiveList() {
      return registerActiveDailyLogSearch(projectId, () => refreshRef.current());
    },
    [projectId],
  );

  /** 복원을 포기하고 목록 첫 부분부터 본다 */
  const abandonRestore = useCallback(() => {
    dismissRefresherError();
    setRestoreState({ scope, phase: "settled" });
  }, [dismissRefresherError, scope]);

  const getDisplayedRequestId = useCallback(() => displayedRequestIdRef.current, []);

  return {
    status: getStatus(),
    items,
    count,
    loadedPageCount,
    errorKind: queryErrorKind,
    isAccessDenied,
    /** 후속 페이지 실패. 이미 받은 행은 유지한다 */
    nextPageErrorKind,
    /** 갱신 실패. 이전 chain은 유지한다 */
    refreshErrorKind,
    /** 캐시 없는 복귀 복원 실패. 받은 지점부터 재시도하거나 처음부터 볼 수 있다 */
    restoreErrorKind,
    isUpdating,
    isRefreshing,
    isFetchingNextPage: query.isFetchingNextPage,
    /** 오프라인 등으로 요청이 대기 중 */
    isPaused: query.fetchStatus === "paused",
    hasNextPage,
    canLoadMore,
    loadMore,
    refresh,
    retryRestore,
    abandonRestore,
    getDisplayedRequestId,
  };

  function getStatus(): DailyLogSearchStatus {
    if (!canSearch && !isAccessDenied) return "idle";
    if (restorePhase === "restoring") return "restoring";
    if (query.data === undefined) {
      if (query.isError) return "error";
      return "loading";
    }
    return "success";
  }
}

export type DailyLogSearchResult = ReturnType<typeof useDailyLogSearch>;
